import React, { createContext, useContext, useState, useEffect } from "react";
import webAuthService from "../services/webAuthService";
import { isMeetingActive } from "../services/meetingSession";

// Create the authentication context
const AuthContext = createContext(null);

// Calls the backend to silently renew the MS Graph access token via the
// server-stored OAuth refresh token.
//
// Returns { profile, fatal }:
//   profile — the updated profile on success, else null.
//   fatal   — true only when the backend positively rejected the session
//             (401/403: refresh token expired, revoked, or never stored).
//             Transient failures (backend restarting, 5xx, network blip,
//             request never left the browser) are NOT fatal and must not
//             end the session — that turned every staging redeploy into a
//             mass logout.
const performSsoRefresh = async () => {
  const ssoRaw = localStorage.getItem("userProfileData");
  if (!ssoRaw) return { profile: null, fatal: true };
  let ssoProfile;
  try {
    ssoProfile = JSON.parse(ssoRaw);
  } catch {
    return { profile: null, fatal: true };
  }
  if (ssoProfile?.source !== "sso" || !ssoProfile?.accessToken) {
    return { profile: null, fatal: true };
  }

  const apiBase = import.meta.env.VITE_API_URL || "http://localhost:8080";
  let resp;
  try {
    resp = await fetch(`${apiBase}/auth/refresh`, {
      method: "POST",
      headers: { Authorization: `Bearer ${ssoProfile.accessToken}` },
    });
  } catch (err) {
    console.warn("[AUTH] Silent refresh unreachable — keeping session:", err.message);
    return { profile: null, fatal: false };
  }

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data.status !== "success" || !data.access_token) {
    const fatal = resp.status === 401 || resp.status === 403;
    console.warn(
      `[AUTH] Silent refresh rejected by backend (${resp.status}):`,
      data.error || "(no detail)",
      fatal ? "— session is no longer valid" : "— transient, keeping session",
    );
    return { profile: null, fatal };
  }

  const updated = { ...ssoProfile, accessToken: data.access_token, loginTime: Date.now() };
  localStorage.setItem("userProfileData", JSON.stringify(updated));
  localStorage.setItem("auth_token", data.access_token);
  console.log("[AUTH] Silent refresh succeeded — session extended");
  return { profile: updated, fatal: false };
};

// A logout that a live meeting has deferred, replayed once the call ends.
// Kept module-level so it survives re-renders of the provider.
let deferredLogout = null;

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuthState = async () => {
      try {
        // Check MSAL authentication
        const authStatus = await webAuthService.getAuthStatus();

        if (authStatus.isAuthenticated && authStatus.account) {
          setUser(authStatus.account);
          setIsAuthenticated(true);
        } else {
          // Fall back to SSO session persisted by SSOCallback
          const ssoRaw = localStorage.getItem("userProfileData");
          if (ssoRaw) {
            try {
              let ssoProfile = JSON.parse(ssoRaw);
              if (ssoProfile?.source === "sso" && ssoProfile?.id) {
                // MS Graph tokens last ~1 hour. Once we're past a safe margin,
                // try to silently renew via the stored refresh token before
                // giving up — only clear the session if that renewal itself
                // fails (refresh token expired/revoked).
                const TOKEN_TTL_MS = 55 * 60 * 1000; // 55 min — safe margin
                if (
                  ssoProfile.loginTime &&
                  Date.now() - ssoProfile.loginTime > TOKEN_TTL_MS
                ) {
                  console.warn("[AUTH] SSO token stale (>55 min) — attempting silent refresh");
                  let refreshed = null;
                  let fatal = true;
                  try {
                    ({ profile: refreshed, fatal } = await performSsoRefresh());
                  } catch (refreshErr) {
                    // Unexpected throw — treat as transient, not as a rejection.
                    console.warn("[AUTH] Silent refresh request failed:", refreshErr.message);
                    fatal = false;
                  }
                  if (!refreshed) {
                    if (fatal) {
                      console.warn("[AUTH] Silent refresh rejected — clearing session");
                      localStorage.removeItem("userProfileData");
                      localStorage.removeItem("auth_token");
                      return;
                    }
                    // Backend down mid-reload: keep the stored session so the
                    // user recovers on the next attempt instead of being sent
                    // back to the login screen.
                    console.warn(
                      "[AUTH] Silent refresh unavailable — keeping stored session for retry",
                    );
                    setUser(ssoProfile);
                    setIsAuthenticated(true);
                    return;
                  }
                  ssoProfile = refreshed;
                }

                setUser(ssoProfile);
                setIsAuthenticated(true);
              }
            } catch {
              localStorage.removeItem("userProfileData");
            }
          }
        }
      } catch (error) {
        console.error("Error checking auth state:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthState();
  }, []);

  // SSO login — called by SSOCallback after backend validates the token
  const ssoLogin = (profile) => {
    setUser(profile);
    setIsAuthenticated(true);
    setError(null);
  };

  // Login function
  const login = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use MSAL Browser authentication
      const result = await webAuthService.loginPopup();

      if (result.success && result.account) {
        setUser(result.account);
        setIsAuthenticated(true);
      } else {
        throw new Error(result.error || "Authentication failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      const userFriendlyMessage = error.message.includes(
        "Authentication window was closed"
      )
        ? "Sign-in was cancelled. Please try again."
        : error.message || "An unexpected error occurred during sign-in.";

      setError(userFriendlyMessage);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use MSAL logout
      const result = await webAuthService.logout();
      if (!result.success) {
        console.warn("Logout warning:", result.error);
      }

      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error("Logout error:", error);
      setError("Failed to sign out completely.");
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      // Always clear all session data — prevents re-login on refresh after logout
      localStorage.removeItem("userProfileData");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      setIsLoading(false);
    }
  };

  // Forced logout (for 401/403 errors)
  const forceLogout = async (reason = "Session expired") => {
    console.warn(`[AUTH] Force logout triggered: ${reason}`);

    try {
      // Clear local state and all session keys immediately
      setIsAuthenticated(false);
      setUser(null);
      setError(reason);
      localStorage.removeItem("userProfileData");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");

      // Force MSAL logout
      await webAuthService.logout();

      // Show browser notification if permitted
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Session Expired", {
          body: reason,
          icon: "/icon.png",
        });
      }
    } catch (error) {
      console.error("[AUTH] Error during force logout:", error);
    }
  };

  // Get access token
  const getAccessToken = async () => {
    try {
      // Get real MSAL access token
      const result = await webAuthService.getAccessToken();
      if (result.success) {
        return result.accessToken;
      } else {
        throw new Error(result.error || "Failed to get access token");
      }
    } catch (error) {
      console.error("Error getting access token:", error);
      // If token fetch fails, user might need to re-authenticate
      if (
        error.message.includes("not authenticated") ||
        error.message.includes("token")
      ) {
        setIsAuthenticated(false);
        setUser(null);
        setError("Your session has expired. Please sign in again.");

        // Force logout on token error
        await webAuthService.logout();
      }
      throw error;
    }
  };

  // Ends the SSO session for real. Wrapped by endSession() so a call in
  // progress can hold it back.
  const clearSsoSession = () => {
    localStorage.removeItem("userProfileData");
    localStorage.removeItem("auth_token");
    setIsAuthenticated(false);
    setUser(null);
  };

  // Ends the session — unless a meeting is live, in which case it's parked
  // and replayed on the next revalidation after the call ends. Getting
  // dropped mid-call costs a rep the conversation; a stale session for the
  // rest of the call costs nothing, since the meeting WebSocket is already
  // authenticated and the worst case is a failed API call inside the app.
  const endSession = (reason) => {
    if (isMeetingActive()) {
      console.warn(`[AUTH] ${reason} — meeting in progress, deferring logout until the call ends`);
      deferredLogout = reason;
      return;
    }
    console.warn(`[AUTH] ${reason} — logging out`);
    clearSsoSession();
  };

  // Proactively renew the SSO access token for the current session before
  // it dies. Returns true if the session is still valid, false if the
  // refresh itself was rejected (and clears the session).
  const refreshTokenIfStale = async () => {
    // A logout parked mid-call: complete it now that the call is over.
    if (deferredLogout && !isMeetingActive()) {
      const reason = deferredLogout;
      deferredLogout = null;
      console.warn(`[AUTH] Meeting ended — applying deferred logout: ${reason}`);
      clearSsoSession();
      return false;
    }

    const ssoRaw = localStorage.getItem("userProfileData");
    if (!ssoRaw) return false;
    try {
      const ssoProfile = JSON.parse(ssoRaw);
      if (!ssoProfile?.accessToken) return false;

      // Proactively renew before the ~60min Graph token actually dies,
      // instead of waiting for it to expire and force-logging the user out
      // mid-session (or mid-meeting). Only a rejected refresh is a real logout.
      const REFRESH_MARGIN_MS = 45 * 60 * 1000;
      if (
        ssoProfile.loginTime &&
        Date.now() - ssoProfile.loginTime > REFRESH_MARGIN_MS
      ) {
        const { profile: refreshed, fatal } = await performSsoRefresh();
        if (!refreshed) {
          if (fatal) {
            endSession("Silent refresh rejected — no valid refresh token on file");
            return false;
          }
          // Backend unreachable or erroring: keep the session and retry on
          // the next tick. The current token may still have minutes left.
          return true;
        }
        setUser(refreshed);
      }
      return true;
    } catch {
      // Fail-open on network errors
      return true;
    }
  };

  // Manually trigger a silent SSO token refresh. Returns true on success.
  const refreshSsoToken = async () => {
    const { profile: refreshed } = await performSsoRefresh();
    if (refreshed) setUser(refreshed);
    return !!refreshed;
  };

  // Context value
  const value = {
    isAuthenticated,
    user,
    isLoading,
    error,
    login,
    logout,
    forceLogout,
    getAccessToken,
    ssoLogin,
    refreshTokenIfStale,
    refreshSsoToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Higher-order component for protecting routes
export const withAuth = (Component) => {
  return (props) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return null; // This will be handled by the main App component
    }

    return <Component {...props} />;
  };
};

export default AuthContext;
